<template>
  <div class="account-adoption-tab">
    <div class="account-adoption-tab__toolbar">
      <div class="account-adoption-tab__toolbar-title">
        <h3 class="account-adoption-tab__title">Adoption</h3>
        <p class="account-adoption-tab__subtitle">
          How deeply this account runs the exIQtive method. Sticky accounts complete key actions
          every week. Updated nightly.
        </p>
      </div>
      <div class="account-adoption-tab__period" role="group" aria-label="Period">
        <button
          v-for="option in periodOptions"
          :key="option.value"
          type="button"
          class="account-adoption-tab__period-btn"
          :class="{ 'account-adoption-tab__period-btn--active': period === option.value }"
          :aria-pressed="period === option.value"
          @click="onPeriodChange(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="account-adoption-tab__state">Loading…</div>
    <div v-else-if="error" class="account-adoption-tab__state account-adoption-tab__state--error">
      {{ error }}
    </div>
    <div v-else-if="!adoption.has_data" class="account-adoption-tab__state">
      Adoption data appears the night after an account is opened.
    </div>
    <div v-else class="account-adoption-tab__content">
      <div class="account-adoption-tab__score-row">
        <StickinessScoreCard
          :score="adoption.score"
          :band="adoption.band"
          :trend="adoption.trend"
          :formula-hint="formulaHint"
        />
        <div class="account-adoption-tab__metrics">
          <article
            v-for="metric in metricCards"
            :key="metric.label"
            class="account-adoption-tab__metric"
          >
            <span class="account-adoption-tab__metric-label">{{ metric.label }}</span>
            <span class="account-adoption-tab__metric-value">{{ metric.display }}</span>
            <span
              v-if="metric.delta != null"
              class="account-adoption-tab__metric-delta"
              :class="`account-adoption-tab__metric-delta--${metric.delta_direction}`"
            >
              {{ deltaText(metric) }}
            </span>
          </article>
        </div>
      </div>

      <div class="account-adoption-tab__signals-row">
        <SignalsCard :groups="signalGroups" />
        <ActivePeopleByRoleCard :roles="adoption.roles" />
      </div>

      <WeeklyActionsCard
        :weeks="adoption.weekly_series.weeks"
        :series="adoption.weekly_series.series"
      />

      <ProductAdoptionTable :products="adoption.products" />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, getCurrentInstance, onMounted, ref } from "vue";
import { getAccountAdoption, mapAdoptionPayload } from "@/api";
import StickinessScoreCard from "./StickinessScoreCard.vue";
import SignalsCard from "./SignalsCard.vue";
import ActivePeopleByRoleCard from "./ActivePeopleByRoleCard.vue";
import WeeklyActionsCard from "./WeeklyActionsCard.vue";
import ProductAdoptionTable from "./ProductAdoptionTable.vue";

const props = defineProps({
  accountId: {
    type: [String, Number],
    required: true,
  },
});

const app = getCurrentInstance();
const { proxy } = app;

const periodOptions = [
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
  { label: "Last 12 months", value: 365 },
];

const period = ref(30);
const loading = ref(true);
const error = ref("");
const payload = ref(null);

const adoption = computed(() => payload.value || mapAdoptionPayload(null));

const metricCards = computed(() => {
  const m = adoption.value.metrics;
  return [
    { ...m.breadth, unit: "%" },
    { ...m.depth, unit: "" },
    { ...m.value, unit: "" },
    { ...m.momentum, unit: "" },
  ].map((metric) => ({
    ...metric,
    display: metricDisplay(metric),
  }));
});

const signalGroups = computed(() => {
  const groups = [];
  const byName = new Map();
  (adoption.value.signals || []).forEach((signal) => {
    const name = signal.group || "Account-wide (people)";
    if (!byName.has(name)) {
      const group = {
        name,
        status_badge: signal.product_status_badge || null,
        signals: [],
      };
      byName.set(name, group);
      groups.push(group);
    }
    const group = byName.get(name);
    if (!group.status_badge && signal.product_status_badge) {
      group.status_badge = signal.product_status_badge;
    }
    group.signals.push(signal);
  });
  return groups;
});

const formulaHint = computed(() => buildFormulaHint(adoption.value.formula));

function metricDisplay(metric) {
  const value = metric.value;
  if (value == null || value === "") return "—";
  if (metric.unit && typeof value === "number") return `${value}${metric.unit}`;
  return String(value);
}

function deltaText(metric) {
  const raw = metric.delta;
  if (raw == null) return "";
  const direction = metric.delta_direction;
  if (direction === "up") return `▲ ${raw}`;
  if (direction === "down") return `▼ ${raw}`;
  return String(raw);
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const { data } = await getAccountAdoption(props.accountId, period.value);
    payload.value = mapAdoptionPayload(data);
  } catch (err) {
    error.value = "Failed to load adoption data. Please try again.";
    proxy.$notifications?.error?.(error.value);
  } finally {
    loading.value = false;
  }
}

function onPeriodChange(value) {
  if (period.value === value) return;
  period.value = value;
  load();
}

function formatWeight(weight) {
  if (weight == null || weight === "") return null;
  const n = Number(weight);
  if (Number.isFinite(n)) {
    const pct = n <= 1 && n !== 0 ? Math.round(n * 100) : Math.round(n);
    return `${pct}%`;
  }
  return null;
}

function buildFormulaHint(formula) {
  const f = formula || {};
  const weights = f.weights || {};

  const weightParts = [
    { key: "breadth", label: "Breadth" },
    { key: "depth", label: "Depth" },
    { key: "value", label: "Value" },
    { key: "momentum", label: "Momentum" },
  ]
    .map((def) => {
      const pct = formatWeight(weights[def.key]);
      return pct ? `${def.label} ${pct}` : null;
    })
    .filter(Boolean);

  const bands = buildBandsPart(f.bands_cutoffs || f.bands || {});

  const parts = [];
  if (weightParts.length) parts.push(weightParts.join(" · "));
  if (bands) parts.push(`Bands: ${bands}`);
  return parts.join(". ");
}

function pickCutoff(cutoffs, keys) {
  for (const key of keys) {
    if (cutoffs[key] != null && cutoffs[key] !== "") return Number(cutoffs[key]);
  }
  return null;
}

function buildBandsPart(cutoffs) {
  if (!cutoffs || !Object.keys(cutoffs).length) return "";
  const sticky = pickCutoff(cutoffs, ["sticky", "sticky_min", "sticky_gte"]);
  const watchMin = pickCutoff(cutoffs, ["watch_min", "watch_low"]);
  const watchMax = pickCutoff(cutoffs, ["watch_max", "watch_high"]);
  const stalling = pickCutoff(cutoffs, ["stalling_max", "stalling_below", "stalling_lt"]);

  const parts = [];
  if (Number.isFinite(sticky)) parts.push(`Sticky ≥ ${sticky}`);
  if (Number.isFinite(watchMin) && Number.isFinite(watchMax)) {
    parts.push(`Watch ${watchMin}–${watchMax}`);
  } else if (Number.isFinite(watchMin) && Number.isFinite(stalling)) {
    parts.push(`Watch ${watchMin}–${stalling - 1}`);
  }
  if (Number.isFinite(stalling)) parts.push(`Stalling < ${stalling}`);
  return parts.join(" · ");
}

onMounted(load);
</script>

<style scoped>
.account-adoption-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0 24px 32px;
}

.account-adoption-tab__toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.account-adoption-tab__toolbar-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 761px;
}

.account-adoption-tab__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
}

.account-adoption-tab__subtitle {
  margin: 0;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.account-adoption-tab__period {
  display: inline-flex;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.account-adoption-tab__period-btn {
  padding: 10px 16px;
  background: #ffffff;
  border: none;
  border-right: 1px solid #d0d5dd;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.account-adoption-tab__period-btn:last-child {
  border-right: none;
}

.account-adoption-tab__period-btn--active {
  background: #f9fafb;
  color: #101828;
}

.account-adoption-tab__period-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.account-adoption-tab__state {
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.account-adoption-tab__state--error {
  color: #b42318;
  background: #fffbfa;
  border-color: #fecdca;
}

.account-adoption-tab__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.account-adoption-tab__score-row {
  display: flex;
  align-items: stretch;
  gap: 16px;
  flex-wrap: wrap;
}

.account-adoption-tab__metrics {
  flex: 1;
  min-width: 560px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.account-adoption-tab__metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.account-adoption-tab__metric-label {
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.account-adoption-tab__metric-value {
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
}

.account-adoption-tab__metric-delta {
  display: inline-flex;
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 9999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.account-adoption-tab__metric-delta--up {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}

.account-adoption-tab__metric-delta--down {
  color: #b42318;
  background: #fee4e2;
  border: 1px solid #fecdca;
}

.account-adoption-tab__metric-delta--flat {
  color: #475467;
  background: #f9fafb;
  border: 1px solid #eaecf0;
}

.account-adoption-tab__signals-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.account-adoption-tab__signals-row > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.account-adoption-tab__signals-row > :last-child {
  flex: 0 0 360px;
  max-width: 360px;
}
</style>
